import { useMemo } from 'react';
import type {
  MapConfig,
  MapLocationsConfig,
  ClientConfig,
  LocationMode,
  MapMode,
} from '@hero/web-components';

export interface UseMapConfigOptions {
  locationsConfig?: MapLocationsConfig;
  mapConfig?: MapConfig;
  clientConfig?: ClientConfig;
}

/**
 * Extract and normalize locations configuration values
 */
function extractLocationsConfig(locationsConfig?: MapLocationsConfig) {
  const markerCountLimitValue = locationsConfig?.countLimit;
  const initialLocationMode: LocationMode = markerCountLimitValue === 1 ? 'single' : 'multi';
  const enableAccurateLocationToggle = locationsConfig?.enableAccurateLocationToggle ?? true;
  const enableSavingPlaces = locationsConfig?.enableSavingPlaces ?? true;
  const enableAccurateLocationIndicator = locationsConfig?.enableAccurateLocationIndicator ?? false;
  const disableEditBusinessCustomer = locationsConfig?.disableEditBusinessCustomer ?? false;
  const mode: MapMode = locationsConfig?.mode ?? 'add_new_location';
  const enterEditModeInitially = locationsConfig?.enterEditModeInitially ?? false;

  return {
    markerCountLimitValue,
    initialLocationMode,
    enableAccurateLocationToggle,
    enableSavingPlaces,
    enableAccurateLocationIndicator,
    disableEditBusinessCustomer,
    mode,
    enterEditModeInitially,
  };
}

/**
 * Extract and normalize map configuration values
 */
function extractMapConfig(mapConfig?: MapConfig) {
  const center = mapConfig?.center;
  const height = mapConfig?.height || '400px';
  const zoom = mapConfig?.zoom || 15;
  const maxZoom = mapConfig?.maxZoom || 18;
  const minZoom = mapConfig?.minZoom || 2;
  const boundsPadding = mapConfig?.boundsPadding || 50;
  const autoFitMarkerBounds = mapConfig?.autoFitMarkerBounds ?? true;
  const disableFullscreen = mapConfig?.disableFullscreen;
  const showUserCurrentLocation = mapConfig?.showUserCurrentLocation ?? false;
  const routeLineType = mapConfig?.routeLineType || 'actual_route';
  const markerIcon = mapConfig?.markerIcon || 'normal_location_icon';

  return {
    center,
    height,
    zoom,
    maxZoom,
    minZoom,
    boundsPadding,
    autoFitMarkerBounds,
    disableFullscreen,
    showUserCurrentLocation,
    routeLineType,
    markerIcon,
  };
}

/**
 * Extract and normalize client configuration values
 */
function extractClientConfig(clientConfig?: ClientConfig) {
  const orgId = clientConfig?.orgId?.toString();
  const customerId = clientConfig?.thirdPartyCustomerId?.toString();

  return {
    orgId,
    customerId,
  };
}

/**
 * Hook to extract and normalize all map configuration values
 * Provides a clean interface for accessing map, locations, and client configuration
 *
 * @param options - Configuration objects from GoogleMapWithMarkerProps
 * @returns Extracted and normalized configuration values
 */
export function useMapConfig(options: UseMapConfigOptions) {
  const { locationsConfig, mapConfig, clientConfig } = options;

  // Extract and normalize locations configuration
  const locationsConfigValues = useMemo(
    () => extractLocationsConfig(locationsConfig),
    [locationsConfig],
  );

  // Extract and normalize map configuration
  const mapConfigValues = useMemo(() => extractMapConfig(mapConfig), [mapConfig]);

  // Extract and normalize client configuration
  const clientConfigValues = useMemo(() => extractClientConfig(clientConfig), [clientConfig]);

  return {
    // Locations configuration
    markerCountLimitValue: locationsConfigValues.markerCountLimitValue,
    initialLocationMode: locationsConfigValues.initialLocationMode,
    enableAccurateLocationToggle: locationsConfigValues.enableAccurateLocationToggle,
    enableSavingPlaces: locationsConfigValues.enableSavingPlaces,
    enableAccurateLocationIndicator: locationsConfigValues.enableAccurateLocationIndicator,
    disableEditBusinessCustomer: locationsConfigValues.disableEditBusinessCustomer,
    mode: locationsConfigValues.mode,
    enterEditModeInitially: locationsConfigValues.enterEditModeInitially,

    // Map configuration
    center: mapConfigValues.center,
    height: mapConfigValues.height,
    zoom: mapConfigValues.zoom,
    maxZoom: mapConfigValues.maxZoom,
    minZoom: mapConfigValues.minZoom,
    boundsPadding: mapConfigValues.boundsPadding,
    autoFitMarkerBounds: mapConfigValues.autoFitMarkerBounds,
    disableFullscreen: mapConfigValues.disableFullscreen,
    showUserCurrentLocation: mapConfigValues.showUserCurrentLocation,
    routeLineType: mapConfigValues.routeLineType,
    markerIcon: mapConfigValues.markerIcon,

    // Client configuration
    orgId: clientConfigValues.orgId,
    customerId: clientConfigValues.customerId,
  };
}
